<template>
  <div>
    <PageHeader
      title="EHR Agentic Suspects"
      subtitle="HCC suspects read from the practice's own EHR documentation by an LLM, with the verbatim evidence"
      :heading-level="2"
    />

    <div class="p-4">
      <!-- Practice filter -->
      <div v-if="summary.length > 0" class="flex flex-wrap gap-3 mb-4">
        <button
          v-for="s in summaryWithAll"
          :key="s.practice_name || 'all'"
          @click="filterByPractice(s.practice_name)"
          class="px-3 py-2 rounded border text-xs font-medium transition-colors text-left"
          :class="activePractice === s.practice_name
            ? 'bg-primary-100 border-primary-300 text-primary-800'
            : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'"
        >
          {{ s.practice_name || 'All practices' }}
          <span class="ml-1.5 font-semibold">{{ s.suspect_count.toLocaleString() }}</span>
          <span class="text-gray-400 font-normal"> / {{ s.patient_count }} pts</span>
        </button>
      </div>

      <!-- Confidence chips + search -->
      <div class="flex items-center justify-between mb-4 gap-3">
        <div class="flex gap-2">
          <button
            v-for="c in ['high', 'medium', 'low']"
            :key="c"
            @click="filterByConfidence(c)"
            class="px-2.5 py-1 rounded border text-xs capitalize"
            :class="activeConfidence === c
              ? 'bg-primary-100 border-primary-300 text-primary-800'
              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'"
          >
            {{ c }}
          </button>
          <button
            v-if="activeConfidence"
            @click="filterByConfidence(null)"
            class="px-2.5 py-1 rounded border border-gray-200 bg-white text-xs text-gray-500 hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
        <div class="flex items-center gap-3">
          <span v-if="generatedAt" class="text-xs text-gray-400">
            Generated {{ formatTimestamp(generatedAt) }}
          </span>
          <button
            @click="exportCsv"
            :disabled="rows.length === 0"
            class="px-3 py-1.5 rounded border border-gray-300 bg-white text-xs hover:bg-gray-50 disabled:opacity-40"
          >
            Export CSV
          </button>
          <input
            v-model="search"
            type="text"
            placeholder="Patient, MBI or ICD..."
            class="px-3 py-1.5 border border-gray-300 rounded text-sm w-56"
            @input="debouncedFetch"
          />
        </div>
      </div>

      <div v-if="loading" class="py-8">
        <LoadingIndicator message="Loading agentic suspects..." />
      </div>

      <div v-else-if="rows.length === 0" class="text-center py-8 text-gray-500 text-sm">
        <p v-if="totalRows === 0 && !search && !activePractice && !activeConfidence">
          No agentic suspects generated yet.
        </p>
        <p v-else>No results match your filters.</p>
      </div>

      <div v-else class="bg-white rounded border border-gray-200 overflow-hidden">
        <table class="min-w-full text-sm">
          <thead class="bg-gray-100">
            <tr>
              <th class="px-3 py-3 text-left font-medium text-gray-700 w-8"></th>
              <th class="px-3 py-3 text-left font-medium text-gray-700">Patient</th>
              <th class="px-3 py-3 text-left font-medium text-gray-700">Age/Sex</th>
              <th class="px-3 py-3 text-left font-medium text-gray-700">PCP</th>
              <th class="px-3 py-3 text-left font-medium text-gray-700">Suspect</th>
              <th class="px-3 py-3 text-left font-medium text-gray-700">HCC</th>
              <th class="px-3 py-3 text-left font-medium text-gray-700">MEAT</th>
              <th class="px-3 py-3 text-left font-medium text-gray-700">Claim</th>
              <th class="px-3 py-3 text-left font-medium text-gray-700">Conf</th>
              <th class="px-3 py-3 text-left font-medium text-gray-700">Evidence</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, idx) in rows" :key="idx">
              <tr
                class="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                @click="expandedRow = expandedRow === idx ? null : idx"
              >
                <td class="px-3 py-3 text-gray-400">
                  <FontAwesomeIcon :icon="expandedRow === idx ? 'chevron-down' : 'chevron-right'" class="w-3 h-3" />
                </td>
                <td class="px-3 py-3">
                  <div class="font-medium text-gray-900">{{ row.patient_name }}</div>
                  <div class="font-mono text-[11px] text-gray-400">{{ row.patient_identifier }}</div>
                </td>
                <td class="px-3 py-3 text-gray-600 whitespace-nowrap">
                  {{ row.patient_age }}{{ row.patient_sex ? ' / ' + row.patient_sex : '' }}
                </td>
                <td class="px-3 py-3 text-gray-600">{{ row.pcp }}</td>
                <td class="px-3 py-3">
                  <span class="font-mono font-semibold text-gray-900">{{ row.suspect_icd10 }}</span>
                  <div class="text-[11px] text-gray-500">{{ row.suspect_description }}</div>
                </td>
                <td class="px-3 py-3">
                  <span class="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 text-[11px] font-medium">
                    {{ row.hcc_v28 }}
                  </span>
                </td>
                <td class="px-3 py-3">
                  <span
                    class="px-1.5 py-0.5 rounded text-[10px] font-medium"
                    :class="meatClass(row.meat_status)"
                  >{{ meatShort(row.meat_status) }}</span>
                </td>
                <td class="px-3 py-3">
                  <span
                    class="px-1.5 py-0.5 rounded text-[10px] font-medium"
                    :class="claimClass(row.claim_status)"
                  >{{ claimLabel(row.claim_status) }}</span>
                </td>
                <td class="px-3 py-3">
                  <span
                    class="px-1.5 py-0.5 rounded text-[11px] font-medium capitalize"
                    :class="confClass(row.final_confidence)"
                  >{{ row.final_confidence }}</span>
                  <div
                    v-if="row.final_confidence !== row.llm_confidence"
                    class="text-[10px] text-gray-400 mt-0.5"
                  >was {{ row.llm_confidence }}</div>
                </td>
                <td class="px-3 py-3 text-gray-600 text-[12px] max-w-md">
                  {{ truncate(row.coder_explanation || row.evidence_summary, 130) }}
                </td>
              </tr>

              <tr v-if="expandedRow === idx" class="bg-gray-50 border-t border-gray-100">
                <td colspan="10" class="px-6 py-4">
                  <div class="grid grid-cols-2 gap-6">
                    <div>
                      <div class="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                        Exact evidence quote from the EHR
                      </div>
                      <div class="bg-yellow-50 border border-yellow-200 rounded p-2 text-[12px] text-gray-800 font-mono whitespace-pre-wrap">
                        {{ row.exact_evidence_quote }}
                      </div>
                      <div class="text-[10px] text-gray-400 mt-1">
                        Quote verification: {{ row.quote_verification }} ·
                        latest evidence {{ row.latest_evidence_date }}
                      </div>

                      <div class="text-[11px] uppercase tracking-wide text-gray-400 mt-3 mb-1">
                        Why it is not already billed
                      </div>
                      <div class="text-[12px] text-gray-700">{{ row.why_not_billed }}</div>
                    </div>

                    <div>
                      <div class="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                        Reasoning
                      </div>
                      <div class="text-[12px] text-gray-700">{{ row.evidence_summary }}</div>

                      <div class="text-[11px] uppercase tracking-wide text-gray-400 mt-3 mb-1">
                        Provenance
                      </div>
                      <div class="text-[11px] text-gray-600 space-y-0.5">
                        <div>Practice: {{ row.practice_name }} (TIN {{ row.tin }})</div>
                        <div>Match: {{ row.match_method }} · EMPI {{ row.empi_patient_master }}</div>
                        <div>ellkay: {{ row.ek_client_id }} / {{ row.ek_patient_id }}</div>
                        <div>
                          Evidence read: {{ row.n_evidence_rows }} structured rows,
                          {{ row.n_notes }} notes · {{ row.billed_hcc_count }} HCCs already billed
                        </div>
                        <div>Model: {{ row.model }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="mt-1 mb-4">
                    <div class="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                      Suggested wording for the provider
                    </div>
                    <div class="bg-white border border-gray-300 rounded p-2 text-[12px] text-gray-800">
                      {{ row.coder_explanation }}
                    </div>
                  </div>

                  <div class="grid grid-cols-3 gap-4">
                    <div class="border rounded p-2 bg-blue-50 border-blue-200">
                      <div class="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
                        MEAT — {{ row.meat_status }}
                      </div>
                      <div class="text-[11px] text-gray-700">{{ row.meat_rationale }}</div>
                      <div v-if="row.confidence_floor_note"
                           class="text-[11px] text-gray-600 mt-1 pt-1 border-t border-blue-200">
                        {{ row.confidence_floor_note }}
                      </div>
                    </div>
                    <div class="border rounded p-2 bg-green-50 border-green-200">
                      <div class="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
                        Never previously coded
                      </div>
                      <div class="text-[11px] text-gray-700">{{ row.novelty_detail }}</div>
                    </div>
                    <div class="border rounded p-2"
                         :class="row.claim_status === 'CLAIM_PRESENT_NO_DX'
                           ? 'bg-green-50 border-green-200'
                           : (row.claim_status === 'NO_CLAIM_IMMATURE'
                              ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-50 border-gray-200')">
                      <div class="text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">
                        Claim check — {{ claimLabel(row.claim_status) }}
                      </div>
                      <div class="text-[11px] text-gray-700">{{ row.claim_lag_detail }}</div>
                    </div>
                  </div>

                  <!-- EVIDENCE — returned by the LLM with the suspect, on the row -->
                  <div class="mt-5 border-t border-gray-200 pt-4">
                    <div class="text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-2">
                      Supporting evidence
                      <span class="text-gray-400 font-normal normal-case">
                        ({{ row.evidence_item_count }} items, each quote verified against the record)
                      </span>
                    </div>

                    <div
                      v-if="!row.supporting_evidence || row.supporting_evidence.length === 0"
                      class="text-xs text-gray-400"
                    >
                      No verified evidence items.
                    </div>

                    <table v-else class="min-w-full text-[11px] border border-gray-200 rounded bg-white">
                      <thead class="bg-gray-50">
                        <tr>
                          <th class="px-2 py-1.5 text-left font-medium text-gray-600 w-24">Date</th>
                          <th class="px-2 py-1.5 text-left font-medium text-gray-600 w-20">Source</th>
                          <th class="px-2 py-1.5 text-left font-medium text-gray-600 w-28">Role</th>
                          <th class="px-2 py-1.5 text-left font-medium text-gray-600">Fact</th>
                          <th class="px-2 py-1.5 text-left font-medium text-gray-600">Verbatim from the record</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(e, i) in row.supporting_evidence" :key="i" class="border-t border-gray-100">
                          <td class="px-2 py-1 align-top text-gray-500 whitespace-nowrap">{{ e.date }}</td>
                          <td class="px-2 py-1 align-top">
                            <span class="px-1 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px]">{{ e.source }}</span>
                          </td>
                          <td class="px-2 py-1 align-top text-gray-500">{{ e.role }}</td>
                          <td class="px-2 py-1 align-top text-gray-800">{{ e.fact }}</td>
                          <td class="px-2 py-1 align-top">
                            <span class="bg-yellow-50 border border-yellow-200 rounded px-1 py-0.5 font-mono text-gray-800 break-all">{{ e.quote }}</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div v-if="totalRows > pageSize" class="flex items-center justify-between mt-3 text-xs text-gray-500">
        <span>
          Showing {{ startRow + 1 }}–{{ Math.min(startRow + rows.length, totalRows) }}
          of {{ totalRows.toLocaleString() }}
        </span>
        <div class="flex gap-2">
          <button
            @click="prevPage"
            :disabled="startRow === 0"
            class="px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40"
          >← Previous</button>
          <button
            @click="nextPage"
            :disabled="startRow + pageSize >= totalRows"
            class="px-3 py-1.5 rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40"
          >Next →</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import PageHeader from '@/components/widgetcomponents/library/PageHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'SuspectRules_AgenticSuspects',
  components: { PageHeader, LoadingIndicator, FontAwesomeIcon },
  data() {
    return {
      summary: [],
      rows: [],
      totalRows: 0,
      generatedAt: null,
      loading: false,
      startRow: 0,
      pageSize: 200,
      activePractice: null,
      activeConfidence: null,
      search: '',
      debounceTimer: null,
      expandedRow: null,
    };
  },
  computed: {
    summaryWithAll() {
      if (this.summary.length === 0) return [];
      const s = this.summary.reduce(
        (a, x) => ({
          suspect_count: a.suspect_count + x.suspect_count,
          patient_count: a.patient_count + x.patient_count,
        }),
        { suspect_count: 0, patient_count: 0 },
      );
      return [{ practice_name: null, ...s }, ...this.summary];
    },
  },
  methods: {
    async fetchData() {
      this.loading = true;
      this.expandedRow = null;
      try {
        const params = { startRow: this.startRow, endRow: this.startRow + this.pageSize };
        if (this.activePractice) params.practice = this.activePractice;
        if (this.activeConfidence) params.confidence = this.activeConfidence;
        if (this.search) params.search = this.search;

        const { data } = await axios.get('/api/coding/suspect-rules/agentic-suspects/', { params });
        this.summary = data.summary || [];
        this.rows = data.rows || [];
        this.totalRows = data.totalRows || 0;
        this.generatedAt = data.generated_at;
      } finally {
        this.loading = false;
      }
    },
    debouncedFetch() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.startRow = 0;
        this.fetchData();
      }, 350);
    },
    filterByPractice(p) {
      this.activePractice = this.activePractice === p ? null : p;
      this.startRow = 0;
      this.fetchData();
    },
    meatShort(m) {
      return {
        'Meat, Present': 'Present',
        'Meat, Missing Plan': 'No plan',
        'Meat, Missing Status': 'No status',
        'Meat, Missing Status and Plan': 'Mention only',
      }[m] || m;
    },
    meatClass(m) {
      if (m === 'Meat, Present') return 'bg-green-100 text-green-800';
      if (m === 'Meat, Missing Status and Plan') return 'bg-gray-100 text-gray-600';
      return 'bg-yellow-100 text-yellow-800';
    },
    claimLabel(cs) {
      return {
        CLAIM_PRESENT_NO_DX: 'Billed, not coded',
        NO_CLAIM_IMMATURE: 'Claim may lag',
        NO_CLAIM_MATURE: 'No claim',
      }[cs] || cs;
    },
    claimClass(cs) {
      if (cs === 'CLAIM_PRESENT_NO_DX') return 'bg-green-100 text-green-800';
      if (cs === 'NO_CLAIM_IMMATURE') return 'bg-yellow-100 text-yellow-800';
      return 'bg-gray-100 text-gray-600';
    },
    filterByConfidence(c) {
      this.activeConfidence = this.activeConfidence === c ? null : c;
      this.startRow = 0;
      this.fetchData();
    },
    prevPage() {
      this.startRow = Math.max(0, this.startRow - this.pageSize);
      this.fetchData();
    },
    nextPage() {
      this.startRow += this.pageSize;
      this.fetchData();
    },
    confClass(c) {
      if (c === 'high') return 'bg-green-100 text-green-800';
      if (c === 'medium') return 'bg-yellow-100 text-yellow-800';
      return 'bg-gray-100 text-gray-600';
    },
    truncate(s, n) {
      if (!s) return '';
      return s.length > n ? `${s.slice(0, n)}…` : s;
    },
    formatTimestamp(ts) {
      if (!ts) return '';
      return new Date(ts).toLocaleString();
    },
    exportCsv() {
      const cols = [
        'patient_name', 'patient_identifier', 'patient_age', 'patient_sex',
        'practice_name', 'tin', 'pcp', 'suspect_icd10', 'suspect_description',
        'hcc_v28', 'llm_confidence', 'evidence_summary', 'exact_evidence_quote',
        'quote_verification', 'why_not_billed', 'latest_evidence_date',
        'n_evidence_rows', 'n_notes', 'billed_hcc_count', 'empi_patient_master',
        'meat_status', 'meat_rationale', 'coder_explanation',
        'novelty_detail', 'claim_status', 'claim_lag_detail',
        'adjusted_confidence', 'final_confidence', 'confidence_floor_note',
        'evidence_date_used',
        'ek_client_id', 'ek_patient_id', 'match_method', 'model',
      ];
      const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
      const csv = [
        cols.join(','),
        ...this.rows.map((r) => cols.map((c) => esc(r[c])).join(',')),
      ].join('\n');
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ehr_agentic_suspects.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>
